export type TopicListSectionKey = 'drafts' | 'active' | 'snoozed' | 'done'
export type TopicAdvanceSectionKey = TopicListSectionKey | 'search'
export type XLinkSourceMode = 'both' | 'xlinks' | 'bookmarks'
export type TopicWorkStatusFilter = 'all' | 'finished' | 'running'

export type TopicFilterCountTopic = {
  id?: string
  closed_at?: number | null
  created_at?: number | null
  closed?: boolean
  is_snoozed?: boolean
  status?: string | null
  unread?: number | null
  inbound_count?: number | null
  source_kind?: string | null
  source_task_id?: string | null
}

export type TopicFilterCounts = {
  unreadMessages: number
  unreadTopics: number
  repliedTopics: number
  finishedTopics: number
  runningTopics: number
  todoistTopics: number
  bookmarkTopics: number
}

export type TopicFilterOpenSections = Record<TopicListSectionKey, boolean>
export type TopicSectionCounts = Record<TopicListSectionKey, number>

export type RecentDoneScopeOptions = {
  recentDoneDays: number
  recentDoneLimit: number
  nowSeconds?: number
}

export type TopicAdvanceContext = {
  chatId: string
  topicId: string
  section: TopicAdvanceSectionKey
  orderedTopicIds: string[]
  savedAt: number
}

export type TopicWorkStatusFlags = {
  finishedOnly: boolean
  runningOnly: boolean
}

const TOPIC_ADVANCE_STORAGE_PREFIX = 'lane-control:topic-advance-context'

function topicAdvanceStorageKey(chatId: string): string {
  return `${TOPIC_ADVANCE_STORAGE_PREFIX}:${chatId}`
}

export function classifyTopic(topic: TopicFilterCountTopic): 'active' | 'snoozed' | 'done' {
  if (topic.closed) return 'done'
  if (topic.is_snoozed) return 'snoozed'
  return 'active'
}

// `hasPendingOutbox` marks a topic with an undelivered message in the local
// outbox. The server-computed `status` cannot know about it (no queued row
// exists yet), so the caller supplies it and the topic counts as running —
// never finished — exactly like a server-queued message.
export function isFinishedTopic(topic: TopicFilterCountTopic, hasPendingOutbox: boolean = false): boolean {
  return !hasPendingOutbox && topic.status !== 'working' && topic.status !== 'queued'
}

export function isRunningTopic(topic: TopicFilterCountTopic, hasPendingOutbox: boolean = false): boolean {
  return hasPendingOutbox || topic.status === 'working' || topic.status === 'queued'
}

export function topicWorkStatusFilterFromFlags(finishedOnly: boolean, runningOnly: boolean): TopicWorkStatusFilter {
  if (runningOnly) return 'running'
  if (finishedOnly) return 'finished'
  return 'all'
}

export function topicMatchesWorkStatusFilter(
  topic: TopicFilterCountTopic,
  filter: TopicWorkStatusFilter,
  hasPendingOutbox: boolean = false,
): boolean {
  if (filter === 'finished') return isFinishedTopic(topic, hasPendingOutbox)
  if (filter === 'running') return isRunningTopic(topic, hasPendingOutbox)
  return true
}

export function nextTopicWorkStatusFlags(
  current: TopicWorkStatusFlags,
  requested: 'finishedOnly' | 'runningOnly',
): TopicWorkStatusFlags {
  if (requested === 'finishedOnly') {
    const finishedOnly = !current.finishedOnly
    return { finishedOnly, runningOnly: finishedOnly ? false : current.runningOnly }
  }

  const runningOnly = !current.runningOnly
  return { finishedOnly: runningOnly ? false : current.finishedOnly, runningOnly }
}

export function isTodoistSourcedTopic(topic: TopicFilterCountTopic): boolean {
  const sourceKind = (topic.source_kind || '').toLowerCase()
  if (sourceKind === 'todoist' || sourceKind === 'todoist_task') return true
  return !!topic.source_task_id && !topic.source_task_id.startsWith('tg_') && sourceKind !== 'x_bookmark'
}

export function isXBookmarkTopic(topic: TopicFilterCountTopic): boolean {
  return (topic.source_kind || '').toLowerCase() === 'x_bookmark'
}

export function countOpenXLinkSourceTopics(
  topics: readonly TopicFilterCountTopic[],
): Pick<TopicFilterCounts, 'todoistTopics' | 'bookmarkTopics'> {
  let todoistTopics = 0
  let bookmarkTopics = 0

  for (const topic of topics) {
    // These badges describe the actionable backlog. Closed source topics stay
    // available under Done as history, but must not make Remove & close look
    // ineffective by remaining in the source count.
    if (topic.closed) continue
    if (isTodoistSourcedTopic(topic)) todoistTopics += 1
    if (isXBookmarkTopic(topic)) bookmarkTopics += 1
  }

  return { todoistTopics, bookmarkTopics }
}

export function topicMatchesXLinkSourceMode(topic: TopicFilterCountTopic, mode: XLinkSourceMode): boolean {
  if (mode === 'xlinks') return isTodoistSourcedTopic(topic)
  if (mode === 'bookmarks') return isXBookmarkTopic(topic)
  return true
}

export function topicListSection(topic: TopicFilterCountTopic, draftAt: number): TopicListSectionKey {
  if (draftAt > 0) return 'drafts'
  return classifyTopic(topic)
}

export function countTopicFiltersForExpandedSections<Topic extends TopicFilterCountTopic>(
  topics: readonly Topic[],
  openSections: TopicFilterOpenSections,
  draftAt: (topic: Topic) => number,
  hasPendingOutbox: (topic: Topic) => boolean = () => false,
): TopicFilterCounts {
  const counts: TopicFilterCounts = {
    unreadMessages: 0,
    unreadTopics: 0,
    repliedTopics: 0,
    finishedTopics: 0,
    runningTopics: 0,
    todoistTopics: 0,
    bookmarkTopics: 0,
  }

  for (const topic of topics) {
    const section = topicListSection(topic, draftAt(topic))
    if (!openSections[section]) continue

    const pendingOutbox = hasPendingOutbox(topic)
    counts.unreadMessages += Math.max(0, topic.unread ?? 0)
    if ((topic.unread ?? 0) > 0) counts.unreadTopics += 1
    if ((topic.inbound_count ?? 0) > 0) counts.repliedTopics += 1
    if (isFinishedTopic(topic, pendingOutbox)) counts.finishedTopics += 1
    if (isRunningTopic(topic, pendingOutbox)) counts.runningTopics += 1
    if (isTodoistSourcedTopic(topic)) counts.todoistTopics += 1
    if (isXBookmarkTopic(topic)) counts.bookmarkTopics += 1
  }

  return counts
}

export function countExpandedTopicRows(
  sectionCounts: TopicSectionCounts,
  openSections: TopicFilterOpenSections,
): number {
  let count = 0
  for (const section of Object.keys(sectionCounts) as TopicListSectionKey[]) {
    if (openSections[section]) count += Math.max(0, sectionCounts[section])
  }
  return count
}

export function compactTopicsForRecentDoneScope<Topic extends TopicFilterCountTopic>(
  topics: readonly Topic[],
  options: RecentDoneScopeOptions,
): Topic[] {
  const recentDoneDays = Math.max(1, Math.floor(options.recentDoneDays))
  const recentDoneLimit = Math.max(0, Math.floor(options.recentDoneLimit))
  const nowSeconds = options.nowSeconds ?? Math.floor(Date.now() / 1000)
  const cutoff = nowSeconds - recentDoneDays * 86_400
  const open: Topic[] = []
  const done: Topic[] = []

  for (const topic of topics) {
    if (topic.closed) done.push(topic)
    else open.push(topic)
  }

  const newestDone = new Set(
    [...done]
      .sort((a, b) => (b.closed_at ?? b.created_at ?? 0) - (a.closed_at ?? a.created_at ?? 0))
      .slice(0, recentDoneLimit),
  )

  return [
    ...open,
    ...done.filter((topic) => {
      if ((topic.unread ?? 0) > 0) return true
      const doneAt = topic.closed_at ?? topic.created_at ?? 0
      return doneAt >= cutoff || newestDone.has(topic)
    }),
  ]
}

export function nextTopicIdAfterRemovingCurrent(
  orderedTopicIds: readonly string[],
  currentTopicId: string,
): string | null {
  const ids = orderedTopicIds.filter((id) => id && id !== currentTopicId)
  if (ids.length === 0) return null
  const currentIndex = orderedTopicIds.indexOf(currentTopicId)
  if (currentIndex < 0) return ids[0] ?? null

  const nextIndex = currentIndex >= ids.length ? ids.length - 1 : currentIndex
  return ids[nextIndex] ?? null
}

export function previousTopicIdBeforeCurrent(
  orderedTopicIds: readonly string[],
  currentTopicId: string,
): string | null {
  return nextTopicIdAfterCurrent([...new Set(orderedTopicIds)].reverse(), currentTopicId)
}

export function nextTopicIdAfterCurrent(
  orderedTopicIds: readonly string[],
  currentTopicId: string,
): string | null {
  const ids = [...new Set(orderedTopicIds.filter(Boolean))]
  if (ids.length <= 1) return null
  const currentIndex = ids.indexOf(currentTopicId)
  if (currentIndex < 0) return ids[0] ?? null
  return ids[(currentIndex + 1) % ids.length] ?? null
}

export function saveTopicAdvanceContext(context: Omit<TopicAdvanceContext, 'savedAt'>): void {
  try {
    localStorage.setItem(topicAdvanceStorageKey(context.chatId), JSON.stringify({
      ...context,
      orderedTopicIds: [...new Set(context.orderedTopicIds.filter(Boolean))],
      savedAt: Date.now(),
    }))
  } catch {/* ignore */}
}

export function loadTopicAdvanceContext(chatId: string): TopicAdvanceContext | null {
  try {
    const raw = localStorage.getItem(topicAdvanceStorageKey(chatId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<TopicAdvanceContext>
    if (
      parsed.chatId !== chatId ||
      typeof parsed.topicId !== 'string' ||
      !Array.isArray(parsed.orderedTopicIds)
    ) {
      return null
    }
    return {
      chatId,
      topicId: parsed.topicId,
      section: parsed.section || 'active',
      orderedTopicIds: parsed.orderedTopicIds.filter((id): id is string => typeof id === 'string' && id.length > 0),
      savedAt: typeof parsed.savedAt === 'number' ? parsed.savedAt : 0,
    }
  } catch {
    return null
  }
}
